import React, { useState, useEffect } from 'react';
import { X, Search, Plus, AlertTriangle, Shield } from 'lucide-react';
import { riskService } from '../../services/riskService';
import RiskEditor from './RiskEditor';
import './RiskSelectorModal.css';

/**
 * Risk Selector Modal
 * Allows selecting existing risk from library OR creating new risk
 */
const RiskSelectorModal = ({ 
  protocolId, 
  protocolName,
  level,  // 'protocol', 'step', or 'action'
  stepId = null,
  stepName = null,
  actionId = null,
  actionName = null,
  onSelect, 
  onClose 
}) => {
  const [activeTab, setActiveTab] = useState('select'); // 'select' or 'create'
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCriticality, setFilterCriticality] = useState('');

  // Load all risks from library
  useEffect(() => {
    const loadRisks = async () => {
      try {
        setLoading(true);
        const data = await riskService.getAllRisks();
        setRisks(data);
      } catch (error) {
        console.error('Error loading risks:', error);
      } finally {
        setLoading(false);
      }
    };
    loadRisks();
  }, []);

  // Filter risks
  const filteredRisks = risks.filter(risk => {
    const matchesSearch = !searchTerm || 
      risk.riskScenario?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      risk.riskDescription?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCriticality = !filterCriticality || risk.criticality === filterCriticality;
    
    return matchesSearch && matchesCriticality;
  });

  const handleSelectRisk = async (risk) => {
    try {
      // Create association
      const association = {
        protocolId,
        protocolName,
        riskId: risk.id,
        riskScenario: risk.riskScenario,
        riskVersion: risk.currentVersion || '1.0',
        level,
        stepId,
        stepName,
        actionId,
        actionName,
        // Copy RPN data for quick access
        severity: risk.severity,
        occurrence: risk.occurrence,
        detection: risk.detection,
        rpn: risk.rpn,
        criticality: risk.criticality
      };

      await riskService.createRiskAssociation(association);
      onSelect && onSelect(association);
    } catch (error) {
      console.error('Error associating risk:', error);
      alert('Failed to associate risk. Please try again.');
    }
  };

  const handleCreateRisk = () => {
    setActiveTab('create');
  };

  const handleRiskCreated = async (newRisk) => {
    // After creating new risk, automatically associate it
    if (newRisk && newRisk.id) {
      await handleSelectRisk(newRisk);
    } else {
      console.error('Risk created but no ID returned:', newRisk);
      alert('Risk was created but association failed. Please try selecting it manually.');
    }
  };

  const getCriticalityColor = (criticality) => {
    switch (criticality?.toLowerCase()) {
      case 'critical': return '#dc2626';
      case 'high': return '#f59e0b';
      case 'medium': return '#8b5cf6';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getContextLabel = () => {
    if (level === 'action' && actionName) return `Action: ${actionName}`;
    if (level === 'step' && stepName) return `Step: ${stepName}`;
    return `Protocol: ${protocolName}`;
  };

  if (!protocolId) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="risk-selector-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2>Associate Risk</h2>
            <p className="context-label">{getContextLabel()}</p>
          </div>
          <button onClick={onClose} className="btn btn-secondary">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="modal-tabs">
          <button
            className={`tab-button ${activeTab === 'select' ? 'active' : ''}`}
            onClick={() => setActiveTab('select')}
          >
            <Shield size={16} />
            Select from Library
          </button>
          <button
            className={`tab-button ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            <Plus size={16} />
            Create New Risk
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {activeTab === 'select' ? (
            <>
              {/* Filters */}
              <div className="risk-filters">
                <div className="filter-group">
                  <Search size={16} className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search risks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>
                <div className="filter-group">
                  <select
                    value={filterCriticality}
                    onChange={(e) => setFilterCriticality(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">All Criticalities</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>

              {/* Risk List */}
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading risks from library...</p>
                </div>
              ) : filteredRisks.length === 0 ? (
                <div className="empty-state">
                  <AlertTriangle size={48} className="empty-icon" />
                  <h3>No Risks Found</h3>
                  <p>
                    {risks.length === 0 
                      ? "No risks in the library yet. Create your first risk!"
                      : "No risks match your search criteria."}
                  </p>
                  <button onClick={handleCreateRisk} className="btn btn-primary">
                    <Plus size={16} />
                    Create New Risk
                  </button>
                </div>
              ) : (
                <div className="risk-library-list">
                  {filteredRisks.map(risk => (
                    <div key={risk.id} className="risk-library-item">
                      <h4 className="risk-name">{risk.riskScenario}</h4>
                      <div className="risk-details">
                        <span className="risk-detail-item">S: {risk.severity}</span>
                        <span className="risk-detail-item">O: {risk.occurrence}</span>
                        <span className="risk-detail-item">D: {risk.detection}</span>
                      </div>
                      {risk.riskDescription && (
                        <span className="risk-description">
                          {risk.riskDescription}
                        </span>
                      )}
                      <div className="risk-badges">
                        <span 
                          className="rpn-badge"
                          style={{ 
                            background: `${getCriticalityColor(risk.criticality)}20`,
                            color: getCriticalityColor(risk.criticality)
                          }}
                        >
                          RPN: {risk.rpn}
                        </span>
                        <span className={`criticality-badge criticality-${risk.criticality?.toLowerCase()}`}>
                          {risk.criticality || 'Unknown'}
                        </span>
                      </div>
                      <button
                        onClick={() => handleSelectRisk(risk)}
                        className="btn btn-primary"
                      >
                        Select
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            /* Create New Risk Tab */
            <div className="create-risk-tab">
              <RiskEditor
                risk={null}
                protocol={protocolId ? { id: protocolId, name: protocolName } : null}
                stepId={stepId}
                actionId={actionId}
                onSave={handleRiskCreated}
                onClose={() => setActiveTab('select')}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        {activeTab === 'select' && (
          <div className="modal-footer">
            <p className="footer-hint">
              💡 Can't find the risk you need? Click "Create New Risk" tab to add it to the library.
            </p>
            <button onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskSelectorModal;

